import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Lightning, ListChecks } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

const DUE_DATE_OPTIONS = [
  { value: '', label: 'No default due date' },
  { value: 'today', label: 'Due today' },
  { value: 'tomorrow', label: 'Due tomorrow' },
  { value: 'in_3_days', label: 'Due in 3 days' },
  { value: 'in_7_days', label: 'Due in 7 days' },
  { value: 'in_14_days', label: 'Due in 2 weeks' },
  { value: 'next_monday', label: 'Due next Monday' },
  { value: 'next_friday', label: 'Due next Friday' },
  { value: 'end_of_week', label: 'Due end of week' },
];

function SaveWorkflowTemplateModal({ task, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '',
    default_title: task?.title || '',
    default_project_client: task?.project_client || '',
    default_priority: task?.priority || 'medium',
    due_date_rule: '',
    notes: task?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const subtasks = task?.subtasks || [];

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.default_title.trim()) return;
    setSaving(true);
    setError('');

    try {
      const payload = {
        name: form.name.trim(),
        default_title: form.default_title.trim(),
        default_project_client: form.default_project_client.trim() || undefined,
        default_priority: form.default_priority,
        due_date_rule: form.due_date_rule || undefined,
        notes: form.notes.trim() || undefined,
        checklist_items: subtasks.map(st => st.title),
      };

      let res;
      if (task?.task_id) {
        res = await axios.post(
          `${API}/tasks/${task.task_id}/save-as-workflow-template`,
          payload,
          { withCredentials: true }
        );
      } else {
        res = await axios.post(`${API}/workflow-templates`, payload, { withCredentials: true });
      }

      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-md w-full max-h-[85vh] overflow-y-auto"
        data-testid="save-workflow-template-modal"
      >
        <div className="border-b border-slate-200 p-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Lightning size={20} weight="duotone" className="text-navy" />
            Save as Workflow Template
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-save-wf">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Template Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Client Onboarding — Lafarge Africa"
              required
              autoFocus
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="wf-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Default Task Title *</label>
            <input
              type="text"
              value={form.default_title}
              onChange={(e) => setForm({ ...form, default_title: e.target.value })}
              required
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="wf-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Default Project / Client</label>
            <input
              type="text"
              value={form.default_project_client}
              onChange={(e) => setForm({ ...form, default_project_client: e.target.value })}
              placeholder="Leave blank if task-specific"
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="wf-project-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Priority</label>
              <select
                value={form.default_priority}
                onChange={(e) => setForm({ ...form, default_priority: e.target.value })}
                className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-3 py-2.5 w-full text-slate-900 text-sm"
                data-testid="wf-priority-input"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Due Date Rule</label>
              <select
                value={form.due_date_rule}
                onChange={(e) => setForm({ ...form, due_date_rule: e.target.value })}
                className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-3 py-2.5 w-full text-slate-900 text-sm"
                data-testid="wf-due-date-rule"
              >
                {DUE_DATE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes / Instructions</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Standard steps, tips, or instructions..."
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm min-h-[60px] resize-none"
              data-testid="wf-notes-input"
            />
          </div>

          {subtasks.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                <ListChecks size={12} /> {subtasks.length} checklist items will be included:
              </p>
              <ul className="space-y-1">
                {subtasks.map((st, i) => (
                  <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" />
                    {st.title}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && <p className="text-xs text-red-600" data-testid="save-wf-error">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={saving || !form.name.trim() || !form.default_title.trim()}
              className="flex-1 bg-navy text-white hover:bg-navy-hover transition-colors font-medium px-4 py-2.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
              data-testid="save-wf-submit"
            >
              <Lightning size={16} weight="fill" />
              {saving ? 'Saving...' : 'Save Workflow Template'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-2.5 rounded-lg text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default SaveWorkflowTemplateModal;
